<template>
  <div class="pan123-token-management">
    <!-- 顶部操作栏 -->
    <div class="operation-bar">
      <el-row :gutter="20" type="flex" align="middle">
        <el-col :span="6">
          <el-input
            v-model="searchForm.token_name"
            placeholder="请输入Token名称"
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="3">
          <el-select v-model="searchForm.is_active" placeholder="启用状态" clearable @change="handleSearch">
            <el-option label="启用" :value="1"></el-option>
            <el-option label="禁用" :value="0"></el-option>
          </el-select>
        </el-col>
        <el-col :span="3">
          <el-select v-model="searchForm.created_by" placeholder="创建者" clearable @change="handleSearch">
            <el-option 
              v-for="user in userList" 
              :key="user.id" 
              :label="user.name" 
              :value="user.id"
            ></el-option>
          </el-select>
        </el-col>
        <el-col :span="12" style="text-align: right">
          <el-button-group>
            <el-button type="primary" icon="el-icon-plus" @click="handleAdd">添加Token</el-button>
            <el-button 
              type="danger" 
              icon="el-icon-delete" 
              @click="handleBatchDelete"
              :disabled="selectedTokens.length === 0"
            >
              批量删除
            </el-button>
          </el-button-group>
        </el-col>
      </el-row>
    </div>

    <!-- Token列表 -->
    <div class="table-container">
      <el-table 
        :data="tokenList" 
        style="width: 100%" 
        v-loading="loading"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="token_name" label="Token名称" min-width="150"></el-table-column>
        <el-table-column prop="token_value" label="Token值" min-width="200">
          <template slot-scope="scope">
            <span class="token-value">{{ maskToken(scope.row.token_value) }}</span>
            <el-button 
              type="text" 
              size="mini" 
              @click="copyToken(scope.row.token_value)"
              style="margin-left: 8px;"
            >
              <i class="el-icon-copy-document"></i>
            </el-button>
          </template>
        </el-table-column>
        <el-table-column prop="usage_count" label="使用次数" width="100" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row.usage_count }}</span>
            <span v-if="scope.row.max_usage_count"> / {{ scope.row.max_usage_count }}</span>
            <span v-else> / ∞</span>
          </template>
        </el-table-column>
        <el-table-column prop="is_active" label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.is_active ? 'success' : 'danger'">
              {{ scope.row.is_active ? "启用" : "禁用" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_by_name" label="创建者" width="100"></el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column prop="last_used_at" label="最后使用" width="160">
          <template slot-scope="scope">
            {{ scope.row.last_used_at ? formatDate(scope.row.last_used_at) : '未使用' }}
          </template>
        </el-table-column>
        <el-table-column prop="ban_until" label="封停解除时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.ban_until && new Date(scope.row.ban_until) > new Date() ? formatDate(scope.row.ban_until) : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" @click="handleUse(scope.row)">使用</el-button>
            <el-button type="text" @click="handleReset(scope.row)">重置</el-button>
            <el-button type="text" class="delete-button" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="50%"
      :before-close="handleDialogClose"
      class="token-dialog"
    >
      <el-form :model="formData" :rules="rules" ref="tokenForm" label-width="120px">
        <el-form-item label="Token名称" prop="token_name">
          <el-input v-model="formData.token_name" placeholder="请输入Token名称"></el-input>
        </el-form-item>
        <el-form-item label="Token值" prop="token_value">
          <el-input 
            v-model="formData.token_value" 
            type="textarea" 
            :rows="3"
            placeholder="请输入123网盘Token值"
          ></el-input>
        </el-form-item>
        <el-form-item label="最大使用次数">
          <el-input-number 
            v-model="formData.max_usage_count" 
            :min="0" 
            placeholder="留空表示无限制"
            style="width: 100%;"
          ></el-input-number>
          <div class="form-tip">留空或设为0表示无限制</div>
        </el-form-item>
        <el-form-item label="启用状态">
          <el-switch 
            v-model="formData.is_active" 
            :active-value="1" 
            :inactive-value="0"
            active-text="启用" 
            inactive-text="禁用"
          ></el-switch>
        </el-form-item>
        <el-form-item label="备注">
          <el-input 
            v-model="formData.remark" 
            type="textarea" 
            :rows="2"
            placeholder="请输入备注信息"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 使用Token对话框 -->
    <el-dialog
      title="使用Token"
      :visible.sync="useDialogVisible"
      width="30%"
      class="use-dialog"
    >
      <el-form :model="useForm" label-width="100px">
        <el-form-item label="Token名称">
          <span>{{ currentToken.token_name }}</span>
        </el-form-item>
        <el-form-item label="当前使用次数">
          <span>{{ currentToken.usage_count }}</span>
          <span v-if="currentToken.max_usage_count"> / {{ currentToken.max_usage_count }}</span>
          <span v-else> / ∞</span>
        </el-form-item>
        <el-form-item label="增加次数">
          <el-input-number v-model="useForm.increment" :min="1" :max="100"></el-input-number>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="useDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleUseSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getPan123Tokens, 
  createPan123Token, 
  updatePan123Token, 
  deletePan123Token, 
  batchDeletePan123Tokens,
  usePan123Token,
  resetPan123Token
} from "@/api";

export default {
  name: "Pan123TokenManagement",
  data() {
    return {
      loading: false,
      tokenList: [],
      userList: [],
      selectedTokens: [],
      searchForm: {
        token_name: "",
        is_active: "",
        created_by: ""
      },
      pagination: {
        currentPage: 1,
        pageSize: 15,
        total: 0
      },
      dialogVisible: false,
      useDialogVisible: false,
      isEdit: false,
      currentToken: {},
      formData: {
        token_name: "",
        token_value: "",
        max_usage_count: null,
        is_active: 1,
        remark: "",
        ban_until: null,
        ban_reason: ""
      },
      useForm: {
        increment: 1
      },
      rules: {
        token_name: [
          { required: true, message: "请输入Token名称", trigger: "blur" }
        ],
        token_value: [
          { required: true, message: "请输入Token值", trigger: "blur" }
        ]
      }
    };
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? "编辑Token" : "添加Token";
    }
  },
  mounted() {
    this.getTokenList();
    this.getUserList();
  },
  methods: {
    async getTokenList() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          ...this.searchForm
        };
        
        // 过滤空值
        Object.keys(params).forEach(key => {
          if (params[key] === "" || params[key] === null) {
            delete params[key];
          }
        });
        
        const response = await getPan123Tokens(params);
        if (response.success) {
          this.tokenList = response.data.tokens;
          this.pagination.total = response.data.pagination.total;
        }
      } catch (error) {
        this.$message.error("获取Token列表失败");
      } finally {
        this.loading = false;
      }
    },
    
    async getUserList() {
      // 这里应该调用获取用户列表的API
      // 暂时使用模拟数据
      this.userList = [
        { id: 1, name: "admin" },
        { id: 2, name: "user1" },
        { id: 3, name: "user2" }
      ];
    },
    
    handleSearch() {
      this.pagination.currentPage = 1;
      this.getTokenList();
    },
    
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.getTokenList();
    },
    
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.getTokenList();
    },
    
    handleSelectionChange(selection) {
      this.selectedTokens = selection;
    },
    
    handleAdd() {
      this.isEdit = false;
      this.formData = {
        token_name: "",
        token_value: "",
        max_usage_count: null,
        is_active: 1,
        remark: "",
        ban_until: null,
        ban_reason: ""
      };
      this.dialogVisible = true;
    },
    
    handleEdit(row) {
      this.isEdit = true;
      this.formData = { 
        ...row,
        ban_until: row.ban_until || null,
        ban_reason: row.ban_reason || ""
      };
      this.dialogVisible = true;
    },
    
    handleUse(row) {
      this.currentToken = row;
      this.useForm.increment = 1;
      this.useDialogVisible = true;
    },
    
    async handleUseSubmit() {
      try {
        const response = await usePan123Token(this.currentToken.id, {
          increment: this.useForm.increment
        });
        if (response.success) {
          this.$message.success(response.data.message);
          this.useDialogVisible = false;
          this.getTokenList();
        }
      } catch (error) {
        this.$message.error("使用Token失败");
      }
    },
    
    async handleReset(row) {
      this.$confirm(`确定要重置Token "${row.token_name}" 的使用次数吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        try {
          const response = await resetPan123Token(row.id);
          if (response.success) {
            this.$message.success(response.data.message);
            this.getTokenList();
          }
        } catch (error) {
          this.$message.error("重置失败");
        }
      });
    },
    
    handleDelete(row) {
      this.$confirm(`确定要删除Token "${row.token_name}" 吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        try {
          const response = await deletePan123Token(row.id);
          if (response.success) {
            this.$message.success("删除成功");
            this.getTokenList();
          }
        } catch (error) {
          this.$message.error("删除失败");
        }
      });
    },
    
    handleBatchDelete() {
      if (this.selectedTokens.length === 0) {
        this.$message.warning("请选择要删除的Token");
        return;
      }
      
      this.$confirm(`确定要删除选中的 ${this.selectedTokens.length} 个Token吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        try {
          const ids = this.selectedTokens.map(token => token.id);
          const response = await batchDeletePan123Tokens({ ids });
          if (response.success) {
            this.$message.success(response.data.message);
            this.getTokenList();
          }
        } catch (error) {
          this.$message.error("批量删除失败");
        }
      });
    },
    
    async handleSubmit() {
      this.$refs.tokenForm.validate(async (valid) => {
        if (valid) {
          try {
            let response;
            if (this.isEdit) {
              response = await updatePan123Token(this.formData.id, this.formData);
            } else {
              response = await createPan123Token(this.formData);
            }
            
            if (response.success) {
              this.$message.success(this.isEdit ? "更新成功" : "创建成功");
              this.dialogVisible = false;
              this.getTokenList();
            }
          } catch (error) {
            this.$message.error(this.isEdit ? "更新失败" : "创建失败");
          }
        }
      });
    },
    
    handleBan(row) {
      this.$prompt('请输入封停原因', '封停Token', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '请输入封停原因',
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return '封停原因不能为空';
          }
          return true;
        }
      }).then(async ({ value }) => {
        // 设置封停时间为7天后
        const banUntil = new Date();
        banUntil.setDate(banUntil.getDate() + 7);
        
        try {
          const response = await updatePan123Token(row.id, {
            ...row,
            ban_until: banUntil.toISOString().slice(0, 19).replace('T', ' '),
            ban_reason: value
          });
          if (response.success) {
            this.$message.success('Token已封停');
            this.getTokenList();
          }
        } catch (error) {
          this.$message.error('封停失败');
        }
      }).catch(() => {
        // 用户取消
      });
    },
    
    handleUnban(row) {
      this.$confirm(`确定要解封Token "${row.token_name}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await updatePan123Token(row.id, {
            ...row,
            ban_until: null,
            ban_reason: null
          });
          if (response.success) {
            this.$message.success('Token已解封');
            this.getTokenList();
          }
        } catch (error) {
          this.$message.error('解封失败');
        }
      });
    },
    
    handleDialogClose() {
      this.$refs.tokenForm.resetFields();
      this.dialogVisible = false;
    },
    
    maskToken(token) {
      if (!token || token.length <= 10) return token;
      return token.substring(0, 6) + "..." + token.substring(token.length - 4);
    },
    
    copyToken(token) {
      navigator.clipboard.writeText(token).then(() => {
        this.$message.success("Token已复制到剪贴板");
      }).catch(() => {
        this.$message.error("复制失败");
      });
    },
    
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString("zh-CN");
    }
  }
};
</script>

<style lang="scss" scoped>
.pan123-token-management {
  padding: 20px;

  .operation-bar {
    background-color: #fff;
    padding: 16px;
    margin-bottom: 16px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    .el-select {
      width: 100%;
    }

    .el-button-group {
      .el-button {
        margin: 0;
      }
    }
  }

  .table-container {
    background-color: #fff;
    padding: 16px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    .token-value {
      font-family: monospace;
      font-size: 12px;
      color: #606266;
    }

    .delete-button {
      color: #f56c6c;
    }
  }

  .pagination-container {
    margin-top: 20px;
    text-align: right;
  }

  .token-dialog {
    .form-tip {
      font-size: 12px;
      color: #909399;
      margin-top: 4px;
    }
  }

  .use-dialog {
    .el-input-number {
      width: 100%;
    }
  }
}
</style>